import { UserType } from "../../../../login/loginTypes"
import { EntityHistory } from "../../../../support/admin/entities/supportEntitiesTypes"
import { IndustryCode } from "../../settings/posAcquirer/SupplierAdminPOSTypes"

export type Merchant = Readonly<{
    id: number
    uid: string
    caseNumber: string
    name: string
    addressDisplayText: string
    isActive: boolean
}>

export type UserTypeDetails = Readonly<{
    id: number
    name: string
    type: UserType
}>

export enum AcquiringType {
    ADYEN = "ADYEN",
}
export enum SalesChannels {
    POS = "POS",
    E_COM = "E-COM",
}
export enum CurrencyCode {
    DKK = "DKK",
}

export enum BankAccountStatus {
    VALID = "VALID",
}

export type MerchantDetailsInfo = Readonly<{
    acquiring: {
        type: AcquiringType.ADYEN
        salesChannel: SalesChannels
        defaultCurrencyCode: CurrencyCode | string
        bankAccount: {
            id: string
            bankName: string
            accountIdentifier: string
            status: BankAccountStatus | string
            refId: string
        }
        industryCodes: IndustryCode[]
        legalEntity: {
            id: string
        }
        balanceAccount: {
            id: string
        }
        store: {
            id: string
        }
    }
}>

export type ContactDetails = Readonly<{
    name: string
    phoneNumber: string
    email: string
}>
export type MerchantAddress = Readonly<{
    street: string
    streetNumber: string
    postalCode: string
    city: string
    countryCode?: string
    country?: {
        code: string
        name?: string
    }
    displayName?: string
}>

export type MerchantDetails = Readonly<{
    type: UserType
    id: number
    uid: string
    caseNumber: string
    name: string
    address: MerchantAddress
    contactDetails: ContactDetails
    entityDetails: {
        connectedSuppliers: UserTypeDetails[]
        connectedCompanies: UserTypeDetails[]
        connectedShops?: UserTypeDetails[]
        connectedDevices: [
            {
                id: number
                name: string
                type: SalesChannels
            },
        ]
    }
    alerts?: [
        {
            value: string
            text: string
        },
    ]
    openingHours?: WeeklySchedule[]
    acquiringDetails: MerchantDetailsInfo | {}
    isActive: boolean
    subscriptionDetails?: SubscriptionDetailsInfo
    history?: EntityHistory
}>

export type RefId = Readonly<{
    refId: string
}>
export type CreateUpdateMerchantFormValues = Readonly<{
    caseNumber: string
    name: string
    address: MerchantAddress
    contactDetails: ContactDetails
    acquiringDetails: AcquiringMerchantDetails
    openingHours?: WeeklySchedule[]
    isActive: boolean
}>

export type OperatingHours = Readonly<{
    open: string
    close: string
}>

export type WeeklySchedule = Readonly<{
    weekDayNumber: number
    weekDay: string
    hours: OperatingHours
}>

export type AcquiringMerchantDetails = Readonly<{
    acquiring?: CreditCardPaymentDetails
}>
interface AcquiringDetails {
    acquiring?: {
        type: AcquiringType.ADYEN | string
        agreement?: {
            id: number
        }
        salesChannels: SalesChannels[]
        defaultCurrencyCode: CurrencyCode | string
        bankAccount?: RefId
        industryCodes?: RefId[]
    }
}

export type CreateUpdateMerchant = Readonly<{
    type: UserType
    caseNumber?: string
    name: string
    address: MerchantAddress
    contactDetails?: ContactDetails
    openingHours?: WeeklySchedule[]
    isActive: boolean
    acquiringDetails?: AcquiringDetails
}>

export type CreditCardPaymentDetails = Readonly<{
    type: AcquiringType.ADYEN | string
    salesChannels: SalesChannels
    defaultCurrencyCode: CurrencyCode | string
    bankAccount: RefId
    industryCodes: string[]
}>
export interface CreditCardPaymentDetailsFormValues extends CreditCardPaymentDetails {
    bankAccountInfo?: BankAccountDetails
}
export type BankAccountDetails = Readonly<{
    status?: BankAccountStatus | string
    bankName?: string
    accountIdentifier?: string
    industryCodes?: string[]
}>

export enum SubscriptionDetailsStatus {
    CREATING = "CREATING",
    ONBOARDING = "ONBOARDING",
    TRIAL = "TRIAL",
    LIVE = "LIVE",
    UNSUBSCRIBING = "UNSUBSCRIBING",
    UNSUBSCRIBED = "UNSUBSCRIBED",
}

type SubscriptionDetailsDocument = Readonly<{
    id: number
    name: string
    url: string
}>

type TimeLine = Readonly<{
    start: string
    end: string
}>

export type SubscriptionDetailsInfo = Readonly<
    | {
          status: SubscriptionDetailsStatus.CREATING
          implementationDeadline: string
      }
    | {
          status: SubscriptionDetailsStatus.ONBOARDING
          implementationDeadline: string
          onboarding: {
              documentsSent: string
              recipients: string[]
              documents: SubscriptionDetailsDocument[]
          }
      }
    | {
          status: SubscriptionDetailsStatus.TRIAL
          implementationDeadline: string
          onboarding: {
              documentsSent: string
              recipients: string[]
              documents: SubscriptionDetailsDocument[]
          }
          trial: TimeLine
      }
    | {
          status: SubscriptionDetailsStatus.LIVE
          implementationDeadline: string
          onboarding: {
              documentsSent: string
              recipients: string[]
              documents: SubscriptionDetailsDocument[]
          }
          trial: TimeLine
          subscription: {
              start: string
          }
      }
    | {
          status: SubscriptionDetailsStatus.UNSUBSCRIBING
          implementationDeadline: string
          onboarding: {
              documentsSent: string
              recipients: string[]
              documents: SubscriptionDetailsDocument[]
          }
          trial: TimeLine
          subscription: TimeLine
      }
    | {
          status: SubscriptionDetailsStatus.UNSUBSCRIBED
          implementationDeadline: string
          onboarding: {
              documentsSent: string
              recipients: string[]
              documents: SubscriptionDetailsDocument[]
          }
          trial: TimeLine
          subscription: TimeLine
      }
>

export type BeginOnboardingFormValues = Readonly<{
    articles: { id: number }[]
    receipients: {
        administrators: { id: number }[]
        extraReceivers: {
            email: string
        }[]
    }
}>

export type MerchantSettingsOnboardingArticle = Readonly<{
    folder: string
    files: {
        id: number
        title: string
        url: string
    }[]
}>

export enum BeginOnboardingType {
    "BEGIN_ONBOARDING" = "BEGIN_ONBOARDING",
}

export type MerchantBeginOnboardingContent = Readonly<{
    step: {
        type: BeginOnboardingType
        onboardingDetails: BeginOnboardingFormValues
    }
}>
